import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger
} from '@/components/ui/hover-card'
import { useConnection } from '@/context/ConnectionContext'
import { cn } from '@/lib/utils'
import { motion } from 'framer-motion'

const STATUS_TEXT = {
  connecting: 'Connecting…',
  live: 'Live',
  reconnecting: 'Reconnecting…',
  offline: 'Offline'
} as const

const STATUS_DOT = {
  connecting: 'bg-amber-500',
  live: 'bg-emerald-500',
  reconnecting: 'bg-amber-500',
  offline: 'bg-zinc-500'
} as const

const STATUS_DESC = {
  connecting: 'Setting up the live connection.',
  live: 'Realtime data is flowing. Tiles, queues, and feeds update automatically.',
  reconnecting:
    "Lost the live connection — retrying. Last-known data is shown until we're back.",
  offline: 'Live updates are paused. Sign in again or check your network.'
} as const

export function PresenceIndicator({ className }: { className?: string }) {
  const { status } = useConnection()
  return (
    <HoverCard openDelay={120}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex items-center gap-2 rounded-full border bg-background px-2.5 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted',
            className
          )}
          aria-label={`Connection: ${STATUS_TEXT[status]}`}
        >
          <span className="relative flex size-2">
            {status === 'live' && (
              <motion.span
                aria-hidden
                className="absolute inset-0 rounded-full bg-emerald-500/40"
                animate={{ scale: [1, 1.8, 1], opacity: [0.6, 0, 0.6] }}
                transition={{
                  duration: 2,
                  repeat: Number.POSITIVE_INFINITY,
                  ease: 'easeOut'
                }}
              />
            )}
            <span
              className={cn('relative size-2 rounded-full', STATUS_DOT[status])}
            />
          </span>
          <span>{STATUS_TEXT[status]}</span>
        </button>
      </HoverCardTrigger>
      <HoverCardContent
        side="bottom"
        align="end"
        className="w-64 text-sm leading-relaxed"
      >
        <p className="font-medium">{STATUS_TEXT[status]}</p>
        <p className="mt-1 text-muted-foreground">{STATUS_DESC[status]}</p>
      </HoverCardContent>
    </HoverCard>
  )
}
